import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkUpsertEmailSubscriptionResponse,
  EmailSubscription,
  EmailsubscriptionEmailSubscriptionChangedEnvelope,
  GenerateUnsubscribeLinkOptions,
  GenerateUnsubscribeLinkResponse,
  QueryEmailSubscriptionsOptions,
  QueryEmailSubscriptionsResponse,
  UpsertEmailSubscriptionOptions,
  UpsertEmailSubscriptionResponse,
  bulkUpsertEmailSubscription as universalBulkUpsertEmailSubscription,
  generateUnsubscribeLink as universalGenerateUnsubscribeLink,
  getEmailSubscription as universalGetEmailSubscription,
  queryEmailSubscriptions as universalQueryEmailSubscriptions,
  upsertEmailSubscription as universalUpsertEmailSubscription,
} from './emailsubscriptions-v1-emailsubscription-email-subscriptions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/email-subscriptions' };

/** @internal */
export function getEmailSubscription(
  httpClient: HttpClient
): GetEmailSubscriptionSignature {
  return (_id: string) =>
    universalGetEmailSubscription(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetEmailSubscriptionSignature {
  /**
   * Get Email Subscription
   * @returns Returned email subscription
   */
  (_id: string): Promise<
    NonNullablePaths<
      EmailSubscription,
      `email` | `subscriptionStatus` | `deliverabilityStatus`,
      2
    >
  >;
}

export function queryEmailSubscriptions(
  httpClient: HttpClient
): QueryEmailSubscriptionsSignature {
  return (
    filter: Record<string, any>,
    options?: QueryEmailSubscriptionsOptions
  ) =>
    universalQueryEmailSubscriptions(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryEmailSubscriptionsSignature {
  /**
   * Retrieves email subscriptions,
   * given the provided paging, filtering, and sorting.
   *
   * Currently, querying is supported on the `email` field
   * with the `$in` array filter.
   * For example, to query for emails `"me@my.com"` and `"you@your.org"`,
   * the filter should be formed like this:
   *
   * ```json
   * { "filter": {
   * "email": {
   * "$in": ["me@my.com", "you@your.org"]
   * }
   * }
   * }
   * ```
   *
   * To learn how to query email subscriptions, see
   * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language).
   * @param - Filter options.
   * Currently, querying is supported on the `email` field
   * with the `$in` array filter.
   */
  (
    filter: Record<string, any>,
    options?: QueryEmailSubscriptionsOptions
  ): Promise<
    NonNullablePaths<
      QueryEmailSubscriptionsResponse,
      | `subscriptions`
      | `subscriptions.${number}.email`
      | `subscriptions.${number}.subscriptionStatus`
      | `subscriptions.${number}.deliverabilityStatus`,
      4
    >
  >;
}

export function upsertEmailSubscription(
  httpClient: HttpClient
): UpsertEmailSubscriptionSignature {
  return (
    options?: NonNullablePaths<
      UpsertEmailSubscriptionOptions,
      `subscription.email`,
      3
    >
  ) =>
    universalUpsertEmailSubscription(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpsertEmailSubscriptionSignature {
  /**
   * Updates or creates an email subscription for the requested email.
   *
   * An email subscription is returned in the response
   * whether it was updated or created.
   */
  (
    options?: NonNullablePaths<
      UpsertEmailSubscriptionOptions,
      `subscription.email`,
      3
    >
  ): Promise<
    NonNullablePaths<
      UpsertEmailSubscriptionResponse,
      | `subscription.email`
      | `subscription.subscriptionStatus`
      | `subscription.deliverabilityStatus`,
      3
    >
  >;
}

export function bulkUpsertEmailSubscription(
  httpClient: HttpClient
): BulkUpsertEmailSubscriptionSignature {
  return (subscriptions: EmailSubscription[]) =>
    universalBulkUpsertEmailSubscription(
      subscriptions,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpsertEmailSubscriptionSignature {
  /**
   * Updates or creates multiple email subscriptions.
   * @param - List of email subscriptions to update or create.
   */
  (subscriptions: EmailSubscription[]): Promise<
    NonNullablePaths<
      BulkUpsertEmailSubscriptionResponse,
      | `results`
      | `results.${number}.originalIndex`
      | `results.${number}.emailSubscription.email`
      | `results.${number}.emailSubscription.subscriptionStatus`
      | `results.${number}.emailSubscription.deliverabilityStatus`
      | `results.${number}.error.errorCode`
      | `results.${number}.error.message`
      | `metadata.totalSuccess`
      | `metadata.totalFailure`,
      5
    >
  >;
}

export function generateUnsubscribeLink(
  httpClient: HttpClient
): GenerateUnsubscribeLinkSignature {
  return (emailAddress: string, options?: GenerateUnsubscribeLinkOptions) =>
    universalGenerateUnsubscribeLink(
      emailAddress,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateUnsubscribeLinkSignature {
  /**
   * Creates an unsubscribe link to be shared with the relevant recipient.
   *
   * If someone clicks the **Unsubscribe** button on the confirmation page,
   * the recipient's `subscriptionStatus` is changed to `UNSUBSCRIBED`.
   * @param - Email address the unsubscribe link is for.
   */
  (emailAddress: string, options?: GenerateUnsubscribeLinkOptions): Promise<
    NonNullablePaths<GenerateUnsubscribeLinkResponse, `link`, 2>
  >;
}

export const onEmailsubscriptionEmailSubscriptionChanged = EventDefinition(
  'com.wixpress.emailsubscriptions.v1.EmailSubscriptionChanged',
  false,
  (event: EmailsubscriptionEmailSubscriptionChangedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.subscription.createdDate' },
            { path: 'data.subscription.updatedDate' },
          ],
        },
      ])
    )
)<EmailsubscriptionEmailSubscriptionChangedEnvelope>();

export {
  AccountInfo,
  ActionEvent,
  BaseEventMetadata,
  BulkUpsertEmailSubscriptionRequest,
  BulkUpsertEmailSubscriptionResponse,
  BulkUpsertEmailSubscriptionResult,
  ConfirmUnsubscribeActionRequest,
  DomainEvent,
  DomainEventBodyOneOf,
  EmailSubscription,
  EmailSubscriptionChanged,
  EmailsubscriptionEmailSubscriptionChangedEnvelope,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Error,
  GenerateUnsubscribeLinkOptions,
  GenerateUnsubscribeLinkRequest,
  GenerateUnsubscribeLinkResponse,
  GetEmailSubscriptionRequest,
  GetEmailSubscriptionResponse,
  HeadersEntry,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  Metadata,
  Paging,
  PagingMetadata,
  QueryEmailSubscriptionsOptions,
  QueryEmailSubscriptionsRequest,
  QueryEmailSubscriptionsResponse,
  RawHttpResponse,
  RenderUnsubscribePageRequest,
  RestoreInfo,
  Status,
  SubscriptionEnumStatus,
  UpdateEmailSubscriptionRequest,
  UpdateEmailSubscriptionResponse,
  UpsertEmailSubscriptionOptions,
  UpsertEmailSubscriptionRequest,
  UpsertEmailSubscriptionResponse,
  V1ConfirmUnsubscribeActionRequest,
  V1RenderUnsubscribePageRequest,
  WebhookIdentityType,
} from './emailsubscriptions-v1-emailsubscription-email-subscriptions.universal.js';
